import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import { fetchDashboardSummary } from '../../services/crm/dashboardService';
import organizationsService from '../../services/crm/organizationsService';
import { CrmLayoutContext } from '../../components/crm/CRMLayout';
import type { CRMHeaderProps } from '../../components/crm/CRMHeader';
import type { CrmQuickAction, CrmMetric } from '../../components/crm/types';
import CustomerCrmHubHeader from './components/CustomerCrmHubHeader';
import CustomerCommandPalette from './components/CustomerCommandPalette';
import OverviewTab from './tabs/OverviewTab';
import ContactsTab from './tabs/ContactsTab';
import CompaniesTab from './tabs/CompaniesTab';
import DealsTab from './tabs/DealsTab';
import ActivitiesTab from './tabs/ActivitiesTab';
import SettingsTab from './tabs/SettingsTab';

type TabId = 'overview' | 'contacts' | 'companies' | 'deals' | 'activities' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.ComponentType;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: '📊', component: OverviewTab },
  { id: 'contacts', label: 'Contacts', icon: '👥', component: ContactsTab },
  { id: 'companies', label: 'Companies', icon: '🏢', component: CompaniesTab },
  { id: 'deals', label: 'Deals', icon: '💰', component: DealsTab },
  { id: 'activities', label: 'Activities', icon: '📅', component: ActivitiesTab },
  { id: 'settings', label: 'Settings', icon: '⚙️', component: SettingsTab }
];

const CustomerCrmHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeOrgId,
    isBootstrapped,
    setActiveOrgId,
    setBootstrapped,
    setCurrentUserId
  } = useCrmOrgStore();
  
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [heroMetrics, setHeroMetrics] = useState<CrmMetric[] | null>(null);
  const [heroError, setHeroError] = useState<string | null>(null);
  const [heroLoading, setHeroLoading] = useState(false);

  // Context state for legacy components that use useCrmLayout
  const defaultHeader = useMemo<CRMHeaderProps>(() => ({
    title: 'Customer CRM',
    subtitle: 'Manage your workspace data',
    showNavigation: false,
  }), []);
  const [headerProps, setHeaderPropsState] = useState<CRMHeaderProps>(defaultHeader);
  const [quickActions, setQuickActions] = useState<CrmQuickAction[]>([]);
  const [metrics, setMetrics] = useState<CrmMetric[]>([]);

  const setHeaderProps = useCallback((props?: Partial<CRMHeaderProps>) => {
    setHeaderPropsState(props ? { ...defaultHeader, ...props } : defaultHeader);
  }, [defaultHeader]);

  // Context value for legacy components
  const contextValue = useMemo(() => ({
    activeOrgId: activeOrgId || null,
    setHeaderProps,
    setQuickActions,
    setMetrics
  }), [activeOrgId, setHeaderProps, setMetrics]);

  // Bootstrap organizations on mount
  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
  }, [user?.id, setCurrentUserId]);

  useEffect(() => {
    const bootstrapOrganizations = async () => {
      if (!user?.id) return;

      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          const createdOrg = created.data;
          if (createdOrg) {
            setActiveOrgId(createdOrg.id);
          }
        } else {
          if (!activeOrgId) {
            setActiveOrgId(list[0]?.id ?? null);
          }
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        toast.error(err?.message || 'Failed to load organizations');
      } finally {
        setBootstrapped(true);
      }
    };

    void bootstrapOrganizations();
  }, [user?.id, activeOrgId, setActiveOrgId, setBootstrapped]);

  useEffect(() => {
    const loadHeroMetrics = async () => {
      if (!activeOrgId) return;
      setHeroLoading(true);
      setHeroError(null);
      try {
        const summary = await fetchDashboardSummary(activeOrgId, { rangeDays: 90 });
        const hero = summary.heroMetrics;
        if (hero) {
          setHeroMetrics([
            { id: 'total-contacts', label: 'Total Contacts', value: new Intl.NumberFormat().format(hero.totalContacts) },
            { id: 'active-companies', label: 'Active Companies', value: new Intl.NumberFormat().format(hero.activeCompanies) },
            { id: 'open-deals', label: 'Open Deals Value', value: `£${(hero.openDealsValue / 1000).toFixed(1)}k` },
            { id: 'this-month', label: 'Deals This Month', value: new Intl.NumberFormat().format(hero.thisMonthDeals) },
            { id: 'conversion-rate', label: 'Win Rate', value: hero.conversionRate !== null ? `${hero.conversionRate}%` : '—' }
          ]);
        } else {
          setHeroMetrics(null);
        }
      } catch (error: any) {
        console.error('Failed to load CRM hero metrics', error);
        setHeroError(error?.message || 'Failed to load metrics');
        setHeroMetrics(null);
      } finally {
        setHeroLoading(false);
      }
    };

    void loadHeroMetrics();
  }, [activeOrgId]);

  // Set document title
  useEffect(() => {
    document.title = 'Customer CRM | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Parse active tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      setActiveTab(hash as TabId);
    }
  }, [location.hash]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Escape to close command palette
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const renderHeroState = () => {
    if (heroLoading) {
      return <div className="py-6 text-center text-sm text-gray-500">Loading workspace metrics…</div>;
    }
    if (heroError) {
      return (
        <div className="py-6 text-center text-sm text-red-600">
          {heroError}
          <button
            type="button"
            className="ml-2 text-primary underline"
            onClick={() => {
              if (activeOrgId) {
                setHeroError(null);
                setHeroLoading(true);
                fetchDashboardSummary(activeOrgId, { rangeDays: 90 })
                  .then((summary) => {
                    const hero = summary.heroMetrics;
                    if (hero) {
                      setHeroMetrics([
                        { id: 'total-contacts', label: 'Total Contacts', value: new Intl.NumberFormat().format(hero.totalContacts) },
                        { id: 'active-companies', label: 'Active Companies', value: new Intl.NumberFormat().format(hero.activeCompanies) },
                        { id: 'open-deals', label: 'Open Deals Value', value: `£${(hero.openDealsValue / 1000).toFixed(1)}k` },
                        { id: 'this-month', label: 'Deals This Month', value: new Intl.NumberFormat().format(hero.thisMonthDeals) },
                        { id: 'conversion-rate', label: 'Win Rate', value: hero.conversionRate !== null ? `${hero.conversionRate}%` : '—' }
                      ]);
                    }
                  })
                  .catch((error: any) => {
                    console.error('Failed to refresh CRM hero metrics', error);
                    setHeroError(error?.message || 'Failed to load metrics');
                  })
                  .finally(() => setHeroLoading(false));
              }
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    if (!heroMetrics) return null;
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {heroMetrics.map((metric) => (
          <div key={metric.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">{metric.label}</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{metric.value}</p>
          </div>
        ))}
      </div>
    );
  };

  if (!isBootstrapped) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-between">
            <div className="animate-pulse text-gray-500">Initializing Customer CRM…</div>
            {heroError && <div className="text-sm text-red-600">{heroError}</div>}
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <CustomerCrmHubHeader />
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100">
          <div className="mx-auto max-w-md">
            <div className="mb-4 text-4xl">🏢</div>
            <h3 className="text-lg font-semibold text-gray-900">No Organization Selected</h3>
            <p className="mt-2 text-sm text-gray-600">
              Please create or select an organization to access the Customer CRM.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || OverviewTab;

  return (
    <CrmLayoutContext.Provider value={contextValue}>
        {/* Tab Content (tabs are now in parent layout) */}
        <div className="space-y-6">
          {renderHeroState()}
          <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6">
            <ActiveTabComponent />
          </div>
        </div>

        {/* Command Palette Hint */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowCommandPalette(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm transition hover:bg-gray-50"
          >
            <span>Press</span>
            <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">⌘K</kbd>
            <span>for quick actions</span>
          </button>
        </div>

      {/* Command Palette */}
      <CustomerCommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={(tabId: string) => {
          handleTabChange(tabId as TabId);
          setShowCommandPalette(false);
        }}
      />
    </CrmLayoutContext.Provider>
  );
};

export default CustomerCrmHub;
